import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Signals to the prerender crawler that this route is fully rendered by:
 *  - waiting until the lazy Suspense boundary that wraps this component has
 *    resolved (we only mount after suspense settles),
 *  - polling per animation frame until react-helmet-async has committed a
 *    `<link rel="canonical">` whose pathname matches the current route
 *    (this guarantees the route-specific `<title>`, `og:*`, and JSON-LD tags
 *    have also been flushed — they live in the same Helmet block),
 *  - only then dispatching `prerender-ready` and setting
 *    `data-prerender-ready="1"` on <html> for the crawler to detect.
 */
const PrerenderSignal = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    let cancelled = false;
    let rafId: number | null = null;

    const expected = pathname.replace(/\/+$/, "") || "/";

    const matches = (): boolean => {
      const root = document.getElementById("root");
      if (!root || root.childElementCount === 0) return false;
      if (!document.title || document.title.trim() === "") return false;

      const canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!canonical || !canonical.href) return false;
      try {
        const canonPath = new URL(canonical.href).pathname.replace(/\/+$/, "") || "/";
        return canonPath === expected;
      } catch {
        return false;
      }
    };

    const tick = () => {
      if (cancelled) return;
      if (matches()) {
        document.dispatchEvent(new CustomEvent("prerender-ready", { detail: { pathname } }));
        document.documentElement.setAttribute("data-prerender-ready", "1");
        return;
      }
      rafId = requestAnimationFrame(tick);
    };

    // Clear any previous marker so route changes force a fresh signal.
    document.documentElement.removeAttribute("data-prerender-ready");
    rafId = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [pathname]);

  return null;
};

export default PrerenderSignal;
